import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import BrandHeader from "../components/brand/BrandHeader";
import Footer from "../components/brand/Footer";
import { formatMoney } from "../lib/brand";
import { addToCart } from "../lib/cart";
import { toast } from "sonner";
import { ShoppingBag, Storefront, ArrowLeft } from "@phosphor-icons/react";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setNotFound(false);
    api.get(`/shop/products/${id}`).then(({ data }) => setP(data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <BrandHeader />
        <section className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="product-not-found">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#0a0a0a] border border-[#D4AF37]/40 text-[#D4AF37]">
            <Storefront size={26} weight="duotone" />
          </div>
          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37] mt-5">Product not found</p>
          <h1 className="font-display text-3xl text-white mt-2">This piece has been retired.</h1>
          <p className="text-white/55 mt-3">The product you&apos;re looking for is no longer available or the link is incorrect.</p>
          <button onClick={() => nav("/shop")} className="tsc-btn-gold mt-6" data-testid="product-not-found-shop">Back to shop</button>
        </section>
        <Footer />
      </div>
    );
  }

  if (!p) return <div className="min-h-screen bg-[#050505]"><BrandHeader /><div className="px-6 py-16 text-white/60">Loading…</div></div>;

  const images = [p.featured_photo, ...(p.gallery || [])].filter(Boolean);
  const heroImg = images[active] || null;

  const handleAdd = () => {
    addToCart(p.id, qty);
    toast.success(`${qty} × ${p.title} added to cart`);
  };

  const buyNow = () => {
    addToCart(p.id, qty);
    nav("/checkout");
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <section className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/shop" className="text-white/55 hover:text-white text-[11px] tracking-luxe uppercase flex items-center gap-1" data-testid="product-back-link">
          <ArrowLeft size={14} /> Back to shop
        </Link>
        <div className="grid lg:grid-cols-2 gap-10 mt-6">
          <div>
            <div className="aspect-square bg-[#0a0a0a] rounded-md overflow-hidden border border-white/5">
              {heroImg ? (
                <img src={heroImg} alt={p.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30">
                  <Storefront size={64} weight="duotone" />
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {images.map((src, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`aspect-square rounded overflow-hidden border ${i === active ? "border-[#D4AF37]" : "border-white/10"}`} data-testid={`product-thumb-${i}`}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">{p.category?.name || "Curated"}</p>
            <h1 className="font-display text-4xl text-white mt-2 leading-tight" data-testid="product-title">{p.title}</h1>
            <div className="mt-5 flex items-baseline gap-3">
              <p className="font-display text-3xl text-white" data-testid="product-price">{formatMoney(p.price)}</p>
              {p.delivery_fee > 0 && <p className="text-white/45 text-sm">+ {formatMoney(p.delivery_fee)} delivery</p>}
            </div>
            {p.short_description && <p className="text-white/75 mt-4">{p.short_description}</p>}
            {p.description && <p className="text-white/55 mt-3 leading-relaxed whitespace-pre-wrap">{p.description}</p>}

            {p.stock !== null && p.stock !== undefined && (
              <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-4" data-testid="product-stock">{p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}</p>
            )}

            <div className="flex items-center gap-3 mt-6">
              <div className="inline-flex items-center border border-white/10 rounded-md overflow-hidden">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 text-white/70 hover:text-white" data-testid="product-qty-dec">−</button>
                <span className="px-4 py-2 font-display text-white text-lg min-w-[3rem] text-center" data-testid="product-qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-3 py-2 text-white/70 hover:text-white" data-testid="product-qty-inc">+</button>
              </div>
              <button onClick={handleAdd} disabled={p.stock === 0} className="tsc-btn-outline flex items-center gap-2 disabled:opacity-50" data-testid="product-add-to-cart">
                <ShoppingBag size={16} weight="duotone" /> Add to cart
              </button>
              <button onClick={buyNow} disabled={p.stock === 0} className="tsc-btn-gold disabled:opacity-50" data-testid="product-buy-now">Buy now</button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
